import {
  BackendError,
  Service,
  SuccessSuccessPagedListResponseSystemString,
} from '@genial/services';
import {
  ConversionInvestmentStrategyDiversifierSuccessPagedListResponse,
  ConversionInvestmentStrategyPositiveSuccessPagedListResponse,
  MetaResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';
import { selector, selectorFamily } from 'recoil';

import { blankMetaResponse } from '../../../utils/blankMetaResponse';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import {
  selectedAccountsPerformanceYearState,
  selectedActivationsByProfileYearState,
  selectedBigNumbersYearState,
  selectedCustomersActivationYearState,
} from '../capture/atom';

import {
  normalizeCustomersActivationByProfileData,
  normalizeCustomersActivationPerformanceData,
  normalizeCustomersHabilitationPerformanceData,
} from './normalize';
import {
  ConversionInvestmentStrategyPositiveSuccessResponseNormalized,
  ConversionAccountHabilitationNormalized,
  ConversionAccountPerformanceActivationNormalized,
  ConversionAccountPerformanceByProfileNormalized,
  CohortChartData,
  ConversionInvestmentStrategyDiversifierSuccessResponseNormalized,
} from './types';

export const investmentStrategyPositiveSelector = selectorFamily<
  | {
      meta: MetaResponse;
      data: ConversionInvestmentStrategyPositiveSuccessResponseNormalized[];
    }
  | null
  | undefined,
  { page?: number; productClass?: string; date?: Date; searchTerm?: string; customerName?: string }
>({
  key: 'homeConversion/investmentStrategyPositiveSelector',
  get:
    ({ page, productClass, date, searchTerm, customerName }) =>
    async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      try {
        let apiResponse: ConversionInvestmentStrategyPositiveSuccessPagedListResponse;
        const limit = 20;

        if (searchTerm) {
          apiResponse = await Service.Home.V1.getConversionInvestmentStrategyPositiveSearchTerm({
            screenName: 'home',
            page: page ?? 1,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            limit,
            searchTerm,
          });
        } else {
          apiResponse = await Service.Home.V1.getConversionInvestmentStrategyPositive({
            screenName: 'home',
            page: page ?? 1,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            productClass,
            customerName,
            date,
            limit,
          });
        }

        const { meta, data } = apiResponse;

        const normalizedData =
          data?.map((item) => ({
            ...item,
            customerNameNormalized: item.customerName ?? '--',
            customerAccountNumberNormalized: item.customerAccountNumber ?? '--',
            customerCustodyAccountNumberNormalized: item.customerPnpAccountNumber ?? '--',
            customerCpfOrCnpjNormalized: Utils.Mask.CpfOrCnpj(item.customerCpfOrCnpj),
            advisorNameNormalized: item.advisorName ?? '--',
            branchNameNormalized: item.branchName ?? '--',
            productClassNormalized: item.productClass ?? '--',
            productNameNormalized: item.productName ?? '--',
            positionValueNormalized: Utils.Mask.Money(item.positionValue),
            positionDateNormalized: Utils.Mask.DateFormat(item.positionDate, 'dd/MM/yyyy'),
            unitPriceNormalized: Utils.Mask.Money(item.unitPrice),
          })) ?? [];

        return { meta, data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const investmentStrategyDiversifierSelector = selectorFamily<
  | {
      meta: MetaResponse;
      data: ConversionInvestmentStrategyDiversifierSuccessResponseNormalized[];
    }
  | null
  | undefined,
  { page?: number; suitability?: string; date?: Date; searchTerm?: string; customerName?: string }
>({
  key: 'homeConversion/investmentStrategyDiversifierSelector',
  get:
    ({ page, suitability, date, searchTerm, customerName }) =>
    async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      try {
        let apiResponse: ConversionInvestmentStrategyDiversifierSuccessPagedListResponse;

        if (searchTerm) {
          apiResponse = await Service.Home.V1.getConversionInvestmentStrategyDiversifierSearchTerm({
            screenName: 'home',
            page: page ?? 1,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            limit: 20,
            searchTerm,
          });
        } else {
          apiResponse = await Service.Home.V1.getConversionInvestmentStrategyDiversifier({
            screenName: 'home',
            page: page ?? 1,
            advisorId: advisorSelected,
            partnerId: partnerSelected,
            date,
            limit: 20,
            suitability,
            customerName,
          });
        }

        const { meta, data } = apiResponse;

        const normalizedData =
          data?.map((item) => ({
            ...item,
            customerNameNormalized: item.customerName ?? '--',
            customerEmailNormalized: item.customerEmail ?? '--',
            customerAccountNumberNormalized: item.customerAccountNumber ?? '--',
            customerCustodyAccountNumberNormalized: item.customerPnpAccountNumber ?? '--',
            customerCpfOrCnpjNormalized: Utils.Mask.CpfOrCnpj(item.customerCpfOrCnpj),
            advisorNameNormalized: item.advisorName ?? '--',
            branchNameNormalized: item.branchName ?? '--',
            stateNormalized: item.state ?? '--',
            suitabilityNormalized: item.suitability ?? '--',
            phoneNormalized: item.phone ?? '--',
            statusNormalized: item.status ?? '--',
            partitionDateNormalized: item.partitionDate
              ? Utils.Mask.DateFormat(item.partitionDate, 'dd/MM/yyyy')
              : '--',
            plValueNormalized: Utils.Mask.Money(item.plValue),
          })) ?? [];

        return { meta, data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const investmentStrategyPositiveProductClassesSelector = selector({
  key: 'homeConversion/investmentStrategyPositiveProductClassesSelector',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const { data } = await Service.Home.V1.getConversionInvestmentStrategyPositiveFilters({
      partnerId: partnerSelected,
      advisorId: advisorSelected,
      screenName: 'home',
    });

    if (!data || !data.products) {
      return [];
    }

    return data.products.map((item) => ({
      label: item,
      value: item,
    }));
  },
});

export const investmentStrategyDiversifierSuitabilitySelector = selector({
  key: 'homeConversion/investmentStrategyDiversifierSuitabilitySelector',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const { data } = await Service.Home.V1.getConversionInvestmentStrategyDiversifierFilters({
      partnerId: partnerSelected,
      advisorId: advisorSelected,
      screenName: 'home',
    });

    if (!data || !data.suitability) {
      return [];
    }

    return data.suitability.map((item) => ({
      label: item,
      value: item,
    }));
  },
});

export const investmentStrategyCustomerSelector = selectorFamily<
  {
    meta: MetaResponse;
    data: string[] | undefined;
  },
  {
    kind: 'positive' | 'diversifier';
    searchTerm?: string;
    page?: number;
  }
>({
  key: 'homeConversion/investmentStrategyPositiveCustomerSelector',
  get:
    ({ searchTerm, page, kind }) =>
    async ({ get }) => {
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      let apiResponse: SuccessSuccessPagedListResponseSystemString;

      try {
        if (kind === 'positive') {
          apiResponse = await Service.Home.V1.getConversionInvestmentStrategyPositiveCustomerFilter(
            {
              advisorId: advisorSelected,
              partnerId: partnerSelected,
              limit: 5,
              page,
              searchTerm,
              screenName: 'home',
            }
          );
        } else {
          apiResponse =
            await Service.Home.V1.getConversionInvestmentStrategyDiversifierCustomerFilter({
              advisorId: advisorSelected,
              partnerId: partnerSelected,
              limit: 5,
              page,
              searchTerm,
              screenName: 'home',
            });
        }

        const { data, meta } = apiResponse;

        if (!data) {
          return { data: [], meta: blankMetaResponse() };
        }

        return { data, meta };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    },
});

export const cohortChartSelector = selector<CohortChartData>({
  key: 'homeConversion/cohortChartSelector',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const { data } = await Service.Home.V1.getConversionAccountPerformanceCohort({
      screenName: 'home',
      advisorId: advisorSelected,
      partnerId: partnerSelected,
    });

    const monthData =
      data.monthlyConversion?.map((item) => {
        const monthObject = new Date(
          `${item.month?.substring(3)}-${item.month?.substring(0, 2)}-01`
        );

        return {
          monthName: item.month ?? '--',
          monthFullName: Utils.Mask.DateFormat(monthObject, 'MMMM/yy'),
          monthObject,
          monthActivationCycle: item.monthActivationCyclePercentage,
          values:
            item.monthlyRefPercentage
              ?.sort((value) => value.monthlyRef)
              ?.map((value) => ({
                value: value.enabledAccountPercentage,
                monthRef: value.monthlyRef,
              })) ?? [],
        };
      }) || [];

    const normalizedData: CohortChartData = {
      months: monthData.sort((a, b) => a.monthObject.getTime() - b.monthObject.getTime()),
      annualAverage: data.annualPercentage,
      average:
        data.averageAnnualPercentageList
          ?.sort((item) => item.averageAnnualRef)
          ?.map((item) => ({
            value: item.averageAnnualPercentage,
            monthRef: item.averageAnnualRef,
          })) ?? [],
    };

    return normalizedData;
  },
});

export const customerConversionFunnel = selector({
  key: 'Home/customerConversionFunnel/Query',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const yearSelected = get(selectedBigNumbersYearState);

    const { data } = await Service.Home.V1.getConversionFunnel({
      screenName: 'home',
      advisorId: advisorSelected,
      partnerId: partnerSelected,
      year: parseInt(yearSelected, 10),
    });

    return { data };
  },
});

export const customersHabilitation = selector<{
  data: ConversionAccountHabilitationNormalized;
}>({
  key: 'Home/customersHabilitation/Query',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const yearSelected = get(selectedAccountsPerformanceYearState);

    const performanceYear = parseInt(yearSelected, 10);

    const { data } = await Service.Home.V1.getConversionAccountPerformanceHabilitation({
      screenName: 'home',
      advisorId: advisorSelected,
      partnerId: partnerSelected,
      year: performanceYear,
    });

    const performanceNormalized = normalizeCustomersHabilitationPerformanceData(
      data.monthlyPerformance
    );

    return { data: { ...data, performanceData: performanceNormalized, performanceYear } };
  },
});

export const customersActivation = selector<{
  data: ConversionAccountPerformanceActivationNormalized;
}>({
  key: 'Home/customersActivation/Query',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const yearSelected = get(selectedCustomersActivationYearState);

    const performanceYear = parseInt(yearSelected, 10);

    const { data } = await Service.Home.V1.getConversionCustomerActivation({
      screenName: 'home',
      advisorId: advisorSelected,
      partnerId: partnerSelected,
      year: performanceYear,
    });

    const performanceNormalized = normalizeCustomersActivationPerformanceData(
      data.monthlyCustomerActivation
    );

    return { data: { ...data, performanceData: performanceNormalized, performanceYear } };
  },
});

export const customersActivationByProfile = selector<{
  data: ConversionAccountPerformanceByProfileNormalized;
}>({
  key: 'Home/customersActivationByProfile/Query',
  get: async ({ get }) => {
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    const yearSelected = get(selectedActivationsByProfileYearState);

    const performanceYear = parseInt(yearSelected, 10);

    const { data } = await Service.Home.V1.getConversionProfileActivation({
      screenName: 'home',
      advisorId: advisorSelected,
      partnerId: partnerSelected,
      year: performanceYear,
    });

    const performanceNormalized = normalizeCustomersActivationByProfileData(
      data.suitabilityMonthlyPerformance
    );

    return { data: { ...data, performanceData: performanceNormalized, performanceYear } };
  },
});
